import { ArrowDown, ArrowUp, ChevronsUp, Minus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { DocStatus, InstallationKind, InstallationStatus, Priority, Severity, StageStatus, TaskStatus, TaskType } from '../types'
import { TASK_STATUS_DOT, TASK_TYPE_ICON } from '../visuals'

function Dot({ className }: { className: string }) {
  return <span className={cn('size-1.5 shrink-0 rounded-full', className)} aria-hidden />
}

export function TaskStatusBadge({ status }: { status: TaskStatus }) {
  const { t } = useTranslation()
  const tone: Record<TaskStatus, BadgeTone> = { todo: 'neutral', in_progress: 'info', review: 'warning', approval: 'primary', done: 'success' }
  return (
    <Badge tone={tone[status]} className={cn(status === 'approval' && 'bg-violet/12 text-violet')}>
      <Dot className={TASK_STATUS_DOT[status]} />
      {t(`projects.taskStatus.${status}`)}
    </Badge>
  )
}

const PRIORITY_ICON = { low: ArrowDown, medium: Minus, high: ArrowUp, urgent: ChevronsUp } satisfies Record<Priority, typeof ArrowUp>
const PRIORITY_TONE: Record<Priority, BadgeTone> = { low: 'success', medium: 'warning', high: 'danger', urgent: 'danger' }

export function PriorityBadge({ priority, compact }: { priority: Priority; compact?: boolean }) {
  const { t } = useTranslation()
  const Icon = PRIORITY_ICON[priority]
  return (
    <Badge tone={PRIORITY_TONE[priority]} className={cn('rounded-md', priority === 'urgent' && 'bg-destructive text-destructive-foreground')}>
      <Icon className="size-3" aria-hidden />
      {compact ? t(`projects.priority.${priority}`) : t('projects.priorityLabel', { value: t(`projects.priority.${priority}`) })}
    </Badge>
  )
}

const TYPE_TONE: Record<TaskType, BadgeTone> = { development: 'primary', installation: 'warning', demo: 'info' }

export function TaskTypeBadge({ type }: { type: TaskType }) {
  const { t } = useTranslation()
  const Icon = TASK_TYPE_ICON[type]
  return (
    <Badge tone={TYPE_TONE[type]} className={cn('rounded-md', type === 'demo' && 'bg-violet/12 text-violet')}>
      <Icon className="size-3" aria-hidden />
      {t(`projects.taskType.${type}`)}
    </Badge>
  )
}

const INST_TONE: Record<InstallationStatus, BadgeTone> = { planned: 'neutral', in_progress: 'info', blocked: 'danger', awaiting_approval: 'primary', done: 'success' }
const INST_DOT: Record<InstallationStatus, string> = { planned: 'bg-muted-foreground', in_progress: 'bg-info', blocked: 'bg-destructive', awaiting_approval: 'bg-violet', done: 'bg-success' }

export function InstallationStatusBadge({ status }: { status: InstallationStatus }) {
  const { t } = useTranslation()
  return (
    <Badge tone={INST_TONE[status]} className={cn(status === 'awaiting_approval' && 'bg-violet/12 text-violet')}>
      <Dot className={INST_DOT[status]} />
      {t(`projects.installStatus.${status}`)}
    </Badge>
  )
}

export function InstallationKindBadge({ kind }: { kind: InstallationKind }) {
  const { t } = useTranslation()
  return (
    <Badge tone={kind === 'installation' ? 'success' : 'info'} className="rounded-md">
      {t(`projects.installKind.${kind}`)}
    </Badge>
  )
}

export function StageStatusBadge({ status }: { status: StageStatus }) {
  const { t } = useTranslation()
  const tone: Record<StageStatus, BadgeTone> = { pending: 'neutral', in_progress: 'info', done: 'success' }
  return <Badge tone={tone[status]}>{t(`projects.stageStatus.${status}`)}</Badge>
}

export function SeverityBadge({ severity }: { severity: Severity }) {
  const { t } = useTranslation()
  const tone: Record<Severity, BadgeTone> = { low: 'neutral', medium: 'warning', high: 'danger' }
  return <Badge tone={tone[severity]} className="rounded-md">{t(`projects.severity.${severity}`)}</Badge>
}

export function DocStatusBadge({ status }: { status: DocStatus }) {
  const { t } = useTranslation()
  const tone: Record<DocStatus, BadgeTone> = { signed: 'success', to_sign: 'warning', waiting: 'neutral' }
  return <Badge tone={tone[status]} className="rounded-md">{t(`projects.docStatus.${status}`)}</Badge>
}
